import { create } from "zustand"

import {
  type AddOnRule,
  type Calibration,
  type CutPin,
  type CutPlans,
  type EditorAddOn,
  type EditorRoom,
  type EditorSurface,
  type MaterialSpec,
  type RoomProduct,
  type RoomSeams,
  SAMPLE_SHEET_KEY,
  type WasteMode,
  type WireAddOn,
  type WireProductAddOn,
  type WireRoom,
  type WireSurface,
  addOnsFromServer,
  calibrationsFromSheets,
  nextRoomSeq,
  nextSurfaceName,
  roomsFromServer,
  rulesFromServer,
  surfacesFromServer,
} from "@/editor-core/document"
import { postJSON, requestJSON } from "@/lib/http"
import { defaultOverlayColor, productFill } from "@/editor-core/overlay"
import { suggestedWaste } from "@/editor-core/patterns"
import {
  FALLBACK_SETTINGS,
  type SeamSettings,
  dragSeam,
  layRing,
  laySpan,
  proposeSeams,
  rollGood,
  usableWidthFt,
} from "@/editor-core/seams"
import {
  type Pt,
  centroid,
  openRing,
  parseFtIn,
  pointInRing,
  rectRing,
  translateEdgePoints,
  translateRing,
} from "@/editor-core/geometry"

export type Mode =
  | "select"
  | "pan"
  | "calibrate"
  | "room"
  | "deduct"
  | "seam"
  | "arrange"
  | "measure"
export type Room = EditorRoom
export type Surface = EditorSurface
export type SaveState = "idle" | "dirty" | "saving" | "saved" | "error"
/** Which of the right panel's three scopes is showing. */
export type PanelScope = "room" | "products" | "takeoff"

/** The material-schedule fields a row carries beyond its product. */
export type SpecPatch = Partial<Pick<MaterialSpec, "subfloor" | "grout" | "notes">>

// The undoable slice: domain state only. View state (mode, selection, status)
// is deliberately outside so undo never yanks the user's tool or camera.
// Rooms span all sheets; calibration is keyed per sheet. pendingRestore is
// the room stashed by a redraw-from-corner (resumeTrace): the original room
// leaves `rooms` while its outline is re-traced as the draft, and comes back
// (with its identity) on close/cancel/undo.
type Snapshot = {
  rooms: Room[]
  // Spec-only schedule rows (shower walls, backsplash). Inside the undoable
  // slice for the same reason rooms are: they are document content, and an
  // estimator who mistypes a surface expects Cmd-Z to fix it.
  surfaces: Surface[]
  // The human's cut-plan decisions (pins + per-cut locks), per product.
  // Undoable and savepointed with everything else — a mis-pinned cut is
  // document content exactly like a mistyped surface.
  cutPlans: CutPlans
  // Room-, surface-, and sheet-scoped add-ons (M8). Document content — a
  // mis-attached add-on expects Cmd-Z exactly like a mistyped surface.
  // Product-scope RULES are deliberately not here: they are server state.
  addOns: EditorAddOn[]
  draft: Pt[]
  calibBySheet: Record<number, Calibration>
  roomSeq: number
  pendingRestore: Room | null
}

type TakeoffState = Snapshot & {
  mode: Mode
  selected: string | null
  // The full (shift-click) selection; always contains `selected` when set.
  // `selected` stays the anchor: handles, inspector, and drags target it.
  selectedIds: string[]
  status: string
  undoStack: Snapshot[]
  redoStack: Snapshot[]

  // Persistence session state (all outside undo).
  activeSheetId: number | null // null = the sample playground (pseudo-sheet 0)
  readOnly: boolean
  docSeq: number // bumps on every domain change — the autosave trigger
  serverRevision: number | null
  saveState: SaveState

  setMode: (mode: Mode) => void
  selectRoom: (id: string | null, opts?: { additive?: boolean }) => void
  setStatus: (status: string) => void

  // Right-panel scope. View state, never undoable. Selecting a row (from the
  // canvas or the takeoff list) jumps to the Room scope; shift-click adds to
  // the multi-selection without yanking the panel away.
  panelScope: PanelScope
  setPanelScope: (scope: PanelScope) => void

  // "Assign to rooms" paint mode: while a product id is armed, clicking a
  // room on the canvas assigns that product instead of selecting. View
  // state; the assignments themselves ride assignProduct (undoable).
  assigningProductId: string | null
  setAssigning: (productId: string | null) => void
  paintProduct: (rowId: string) => void

  /**
   * Patch the product snapshot on EVERY row sharing this catalog id — the
   * Products scope's "change it here and every room using it follows"
   * contract, in one undo step (same shape as setProductColor).
   */
  updateProductEverywhere: (productId: string, patch: Partial<RoomProduct>) => void

  addVertex: (pt: Pt) => void
  removeLastVertex: () => void
  cancelDraft: () => void
  closeDraft: () => void
  /** The drag-to-rectangle trace gesture: commit the axis-aligned rectangle
   *  between two diagonal corners as if its four corners had been clicked —
   *  same naming, redraw-restore, deduction targeting, and single undo step
   *  as closeDraft, because it IS closeDraft. Only from an empty draft. */
  commitRect: (a: Pt, b: Pt) => void

  setCalibPoint: (pt: Pt) => void
  applyCalibration: (input: string) => void
  /** Set the active sheet's scale from a preset (1/4" = 1'-0" → 1/75 ft/px)
      — a calibration without the clicks, autosaved like one. */
  applyPresetScale: (ftPerPx: number, label: string) => void
  /** Finish a CROP calibration: the sheet's ft_per_px stands untouched (the
      crop rescaled to satisfy it, via ScaleControls' patch); only the
      transient clicked points clear, plus a status saying what happened. */
  completeCropCalibration: (status: string) => void
  /**
   * The arrange tool's half of the rescale contract: when an image's scale
   * changes, the SERVER clears the sheet's calibration (SheetImage callback),
   * and the client drops its local copy so a later autosave — which can only
   * write calibrations, never clear them — doesn't resurrect the stale value.
   * Deliberately NOT undoable and NOT dirty-marking: it mirrors a change the
   * server already made, so there is nothing to save. (Undoing past this
   * point can still restore the old calibration from a snapshot — a narrow,
   * self-inflicted edge; recalibrating fixes it.)
   */
  clearSheetCalibration: (sheetId: number) => void

  /** Rooms and surfaces share the name field, so one action serves both. */
  renameRow: (id: string, name: string) => void
  setWaste: (id: string, waste: number) => void
  deleteRoom: (id: string) => void
  moveVertex: (id: string, ring: "outline" | number, index: number, pt: Pt) => void
  insertVertex: (id: string, ring: "outline" | number, edgeIndex: number, pt: Pt) => void
  deleteVertex: (id: string, ring: "outline" | number, index: number) => void
  translateEdge: (id: string, ring: "outline" | number, edgeIndex: number, dx: number, dy: number) => void
  translateRoom: (id: string, dx: number, dy: number) => void
  /**
   * Open one of the two walls meeting at the given outline corner ("prev" =
   * the wall from the previous corner, "next" = the wall to the next). All
   * corners survive as an open draft ending at the selected corner, ready
   * for further tracing; closing recreates the room with its identity.
   */
  openWall: (id: string, index: number, side: "prev" | "next") => void

  // Focus mode: edit one room in isolation (everything else dims and stops
  // listening). View state, never undoable — like `selected`.
  focusedRoomId: string | null
  setFocusedRoom: (id: string | null) => void

  // Product assignment. Every one of these takes a ROW id — a room's or a
  // surface's — because a shower wall carries the same product snapshot a
  // floor does. assignProduct covers one-or-many rows in a single undo step;
  // updateProduct patches dims/pattern/angle (a pattern change also applies
  // the suggested waste, which is a no-op on a surface). productPreview is
  // the live angle-drag overlay — view state, never undoable, never persisted.
  assignProduct: (rowIds: string[], product: RoomProduct) => void
  updateProduct: (id: string, patch: Partial<RoomProduct>) => void
  /** Recolor every row whose product snapshot shares this catalog id. */
  setProductColor: (productId: string, color: string) => void
  /**
   * Live overlay appearance drag (custom color, fill opacity) — view state,
   * never undoable, never persisted; committing on release is one undo step.
   * Mirrors productPreview, which does the same job for the angle slider.
   */
  overlayPreview: { productId: string; color: string; fill: number } | null
  setOverlayPreview: (preview: { productId: string; color: string; fill: number } | null) => void
  commitOverlayPreview: () => void
  clearProduct: (id: string) => void
  productPreview: { roomId: string; angle: number } | null
  setProductPreview: (preview: { roomId: string; angle: number } | null) => void

  // Material-schedule spec: subfloor override, grout, and the notes list.
  // Row-aware for the same reason the product actions are.
  setSpec: (id: string, patch: SpecPatch) => void

  // Quantity refinements (rooms only — surfaces have no geometry).
  setWasteMode: (id: string, mode: WasteMode | null) => void
  setMultiplier: (id: string, multiplier: number) => void

  // Seam planning. Company estimating policy rides outside the undo slice —
  // it is server state, not document state; the sample playground falls back
  // to the migration defaults.
  settings: SeamSettings
  setSettings: (settings: SeamSettings) => void
  /**
   * Selected seam element for keyboard delete / lock toggles. View state,
   * like `selected` — a seam is part of a room, not a schedule row.
   */
  selectedSeam: { roomId: string; kind: "offset" | "cross"; index: number } | null
  selectSeam: (seam: { roomId: string; kind: "offset" | "cross"; index: number } | null) => void
  /** Propose (or re-propose) the initial layout. Generator action: skipped
   *  when the room's seam lock is on. */
  proposeSeamLayout: (roomId: string) => void
  clearSeams: (roomId: string) => void
  switchFillSide: (roomId: string) => void
  setLayDirection: (roomId: string, angle: number) => void
  addSeamAt: (roomId: string, posFt: number) => void
  addCrossSeamAt: (roomId: string, strip: number, posFt: number) => void
  removeSeam: (roomId: string, kind: "offset" | "cross", index: number) => void
  toggleSeamLock: (roomId: string, index: number) => void
  toggleRoomSeamLock: (roomId: string) => void
  /** Live seam drag overlay — view state, never undoable, never persisted;
   *  commit on release is one undo step, mirroring productPreview. */
  seamPreview: { roomId: string; seams: RoomSeams } | null
  dragSeamPreview: (roomId: string, offsetIndex: number, targetFt: number) => void
  /** Live T-seam drag — same preview slot, no push mechanics involved. */
  dragCrossPreview: (roomId: string, crossIndex: number, posFt: number) => void
  commitSeamPreview: () => void
  cancelSeamPreview: () => void

  /**
   * Place a cut by hand — the tray drag's commit. `pin` carries the roll
   * and the 2D position on it; null releases the cut back to the
   * allocator. Pin = placed: everything unpinned re-flows around pins.
   * Undoable.
   */
  pinCut: (productId: string, cutKey: string, pin: CutPin | null) => void

  // Spec-only surfaces. `selectedSurfaceId` is deliberately separate from the
  // room selection: a surface has no geometry, so it must never reach the
  // canvas handles, drags, or focus mode. Selecting one clears the other.
  selectedSurfaceId: string | null
  selectSurface: (id: string | null) => void
  addSurface: (parentRoomId: string | null) => void
  deleteSurface: (id: string) => void
  /** The surface's typed order quantity (its product's unit). Undoable. */
  setSurfaceQty: (id: string, qty: number | null) => void

  // Takeoff add-ons (room / surface / sheet scope). Document content —
  // undoable, autosaved, savepointed. The store assigns the client id.
  addAddOn: (draft: Omit<EditorAddOn, "id">) => void
  updateAddOn: (id: string, patch: Partial<Omit<EditorAddOn, "id">>) => void
  removeAddOn: (id: string) => void

  /**
   * Product-scope rules — SERVER state (the settings precedent), outside the
   * undo slice: Cmd-Z after a rule edit undoes document changes only. Writes
   * are optimistic; the endpoint returns the full authoritative list and a
   * failure rolls the local list back without touching the takeoff.
   */
  addOnRules: AddOnRule[]
  setAddOnRules: (rules: WireProductAddOn[]) => void
  saveAddOnRule: (
    draft: Omit<AddOnRule, "id" | "position"> & { id?: number | null }
  ) => Promise<{ ok: boolean; errors?: Record<string, string> }>
  deleteAddOnRule: (id: number) => Promise<void>
  /**
   * Place a schedule row immediately after another one — the single reorder
   * action behind both the drag handles and the keyboard move buttons.
   *
   * A surface dropped after a room becomes that room's first surface; after
   * another surface it becomes its next sibling; with `null` it detaches and
   * prints at the tail. A room moves with its surfaces in tow, and `null`
   * sends it to the top.
   *
   * Re-parenting IS the reorder, so row order keeps exactly one source and
   * cannot drift out of sync with the drawing.
   */
  moveRowAfter: (id: string, afterRowId: string | null) => void

  undo: () => void
  redo: () => void
  reset: () => void

  hydrate: (args: {
    rooms: WireRoom[]
    surfaces?: WireSurface[] | null
    addOns?: WireAddOn[] | null
    cutPlans?: CutPlans | null
    sheets: { id: number; ft_per_px: number | null; calib_pts: Pt[] | null }[]
    revision: number
    activeSheetId: number | null
  }) => void
  setActiveSheet: (id: number | null) => void
  setReadOnly: (readOnly: boolean) => void
  markSaving: () => void
  markSaved: (revision: number, savedSeq: number) => void
  /** `detail` is the server's own sentence (422 validation) — it lands in
      the status line so the estimator knows WHAT to fix. */
  markSaveError: (detail?: string) => void
}

const MAX_UNDO = 200

const initialSnapshot: Snapshot = {
  rooms: [],
  surfaces: [],
  cutPlans: {},
  addOns: [],
  draft: [],
  calibBySheet: {},
  roomSeq: 0,
  pendingRestore: null,
}

const EMPTY_CALIBRATION: Calibration = { ftPerPx: null, calibPts: [] }

const takeSnapshot = (s: Snapshot): Snapshot => ({
  rooms: s.rooms,
  surfaces: s.surfaces,
  cutPlans: s.cutPlans,
  addOns: s.addOns,
  draft: s.draft,
  calibBySheet: s.calibBySheet,
  roomSeq: s.roomSeq,
  pendingRestore: s.pendingRestore,
})

export const MODE_STATUS: Record<Mode, string> = {
  select: "Click a room to select it — drag to move, double-click to edit in isolation",
  pan: "Drag to pan, scroll to zoom (or hold the scroll wheel in any tool)",
  calibrate:
    "Click both ends of a known dimension — use extension-line intersections, not dimension-line endpoints",
  room: "Click vertices; click the first point again (or double-click) to close",
  deduct: "Trace a deduction inside a room",
  seam: "Drag a seam to move it — double-click inside a strip to add one, Backspace removes the selected seam",
  arrange: "Drag a floor plan to place it — set its scale in the panel",
  measure: "Measure — click two points to read the distance; Esc clears",
}

/**
 * The re-solve contract's client half: when a room's seam or outline
 * geometry changes, every cut it produces changes shape, so its pins and
 * locks are stale and drop; other rooms' pinned cuts stay fixed obstacles.
 * (Refinements 2026-08-06, adopted from the teardown verbatim.)
 */
const pruneRoomPins = (plans: CutPlans, roomId: string, keep?: (key: string) => boolean): CutPlans => {
  const stale = (key: string) => key.startsWith(`${roomId}:`) && !(keep?.(key) ?? false)
  const out: CutPlans = {}
  for (const [productId, plan] of Object.entries(plans)) {
    const pins = Object.fromEntries(Object.entries(plan.pins).filter(([key]) => !stale(key)))
    const locks = plan.locks.filter((key) => !stale(key))
    if (Object.keys(pins).length || locks.length) out[productId] = { pins, locks }
  }
  return out
}

export const useTakeoffStore = create<TakeoffState>()((set, get) => {
  // Commit-once guard for the click+dblclick double-fire (spike lesson #3).
  // Zustand reads fresh state so the second call already sees an empty draft,
  // but the reference check keeps the guarantee explicit and free.
  let lastCommitted: Pt[] | null = null

  // Monotonic counter over rule mutations — only the latest one may write
  // the authoritative list back (responses can complete out of order).
  let ruleMutationSeq = 0

  const sheetKey = () => get().activeSheetId ?? SAMPLE_SHEET_KEY

  const activeCalibration = () => get().calibBySheet[sheetKey()] ?? EMPTY_CALIBRATION

  // Every domain mutation goes through here: push undo, clear redo, apply,
  // mark dirty. Read-only mode bails at this single choke point, which makes
  // a viewer structurally unable to change the document.
  const mutate = (changes: Partial<TakeoffState>) => {
    if (get().readOnly) return
    set((s) => ({
      undoStack: [...s.undoStack.slice(-(MAX_UNDO - 1)), takeSnapshot(s)],
      redoStack: [],
      docSeq: s.docSeq + 1,
      saveState: "dirty",
      ...changes,
    }))
  }

  const setCalibration = (calib: Calibration, extra: Partial<TakeoffState>) =>
    mutate({
      calibBySheet: { ...get().calibBySheet, [sheetKey()]: calib },
      ...extra,
    })

  // A cleared draft that was a redraw-in-progress puts the original room
  // back; every draft-discarding path goes through this.
  const restoredRooms = (s: TakeoffState) =>
    s.pendingRestore ? [ ...s.rooms, s.pendingRestore ] : s.rooms

  // A schedule row is a room OR a surface, and everything on the spec side
  // (product, grout, subfloor, notes) applies identically to both. One mapper
  // over both collections keeps that a single definition instead of two
  // parallel action sets that would drift apart field by field.
  const mapRows = <T extends Partial<{ rooms: Room[]; surfaces: Surface[] }>>(
    match: (row: Room | Surface) => boolean,
    patch: (row: Room | Surface) => Room | Surface
  ): T =>
    ({
      rooms: get().rooms.map((r) => (match(r) ? (patch(r) as Room) : r)),
      surfaces: get().surfaces.map((s) => (match(s) ? (patch(s) as Surface) : s)),
    }) as T

  // One seam-geometry edit shape: patch the room's seams, mark them
  // hand-edited (auto: false — the visible auto-lock escalation), and drop
  // the room's now-stale cut pins in the SAME undo step.
  const seamEdit = (
    roomId: string,
    patch: (seams: RoomSeams) => RoomSeams,
    status: string
  ) => {
    const s = get()
    const room = s.rooms.find((r) => r.id === roomId)
    if (!room?.seams) return
    mutate({
      rooms: s.rooms.map((r) =>
        r.id === roomId ? { ...r, seams: { ...patch(r.seams!), auto: false } } : r
      ),
      cutPlans: pruneRoomPins(s.cutPlans, roomId),
      status,
    })
  }

  const roomFtPerPx = (room: Room): number | null =>
    get().calibBySheet[room.sheetId]?.ftPerPx ?? null

  return {
    ...initialSnapshot,
    mode: "select",
    selected: null,
    selectedIds: [],
    status: "Pick a tool to begin",
    undoStack: [],
    redoStack: [],
    activeSheetId: null,
    readOnly: false,
    docSeq: 0,
    serverRevision: null,
    saveState: "idle",
    productPreview: null,
    focusedRoomId: null,
    selectedSurfaceId: null,
    settings: FALLBACK_SETTINGS,
    selectedSeam: null,
    seamPreview: null,
    overlayPreview: null,
    panelScope: "takeoff",
    assigningProductId: null,

    setMode: (mode) => {
      if (get().readOnly) return
      // While focused, only select/pan/deduct make sense — tracing a new room,
      // recalibrating, or rearranging would silently break out of the
      // isolation (and a focused room means rooms exist, so layout is locked
      // anyway).
      if (get().focusedRoomId && (mode === "room" || mode === "calibrate" || mode === "arrange"))
        return
      const s = get()
      // Paint mode rides the select tool's listeners; any other tool ends it.
      const assign = mode === "select" ? {} : { assigningProductId: null }
      if (s.draft.length > 0) {
        mutate({
          draft: [],
          rooms: restoredRooms(s),
          pendingRestore: null,
          mode,
          status: MODE_STATUS[mode],
          ...assign,
        })
      } else set({ mode, status: MODE_STATUS[mode], ...assign })
    },

    setPanelScope: (scope) => set({ panelScope: scope }),

    setAssigning: (productId) => {
      if (productId == null) {
        set({ assigningProductId: null, status: MODE_STATUS[get().mode] })
        return
      }
      if (get().readOnly) return
      get().setMode("select")
      set({
        assigningProductId: productId,
        panelScope: "products",
        status: "Click rooms on the plan to apply the product — Done to finish",
      })
    },

    paintProduct: (rowId) => {
      const s = get()
      const productId = s.assigningProductId
      if (!productId) return
      const snapshot = [...s.rooms, ...s.surfaces].find(
        (r) => r.product?.id === productId
      )?.product
      if (!snapshot) {
        set({ assigningProductId: null })
        return
      }
      const target = [...s.rooms, ...s.surfaces].find((r) => r.id === rowId)
      // Re-clicking a painted room is a no-op, not an empty undo step.
      if (!target || target.product?.id === productId) return
      s.assignProduct([rowId], snapshot)
    },

    updateProductEverywhere: (productId, patch) => {
      const waste =
        patch.pattern !== undefined
          ? suggestedWaste(patch.pattern, get().settings.wasteSuggestions)
          : null
      mutate(
        mapRows(
          (r) => r.product?.id === productId,
          (r) => ({
            ...r,
            product: { ...r.product!, ...patch },
            ...(waste != null ? { waste } : {}),
          })
        )
      )
    },

    // Selection is pinned to the focused room while focus mode is active —
    // sidebar row clicks on other rooms would otherwise desync the handles.
    // additive (shift-click) toggles the room in/out of the multi-selection;
    // the anchor follows the most recent addition.
    selectRoom: (id, opts) => {
      const { focusedRoomId, selectedIds } = get()
      if (focusedRoomId && id !== focusedRoomId) return
      if (id === null) {
        set({ selected: null, selectedIds: [] })
        return
      }
      // One inspector, one subject: picking a room releases the surface.
      set({ selectedSurfaceId: null })
      if (opts?.additive) {
        // Additive selection is a totals gesture — it never yanks the panel.
        if (selectedIds.includes(id)) {
          const next = selectedIds.filter((x) => x !== id)
          set({ selectedIds: next, selected: next[next.length - 1] ?? null })
        } else {
          set({ selectedIds: [...selectedIds, id], selected: id })
        }
        return
      }
      set({ selected: id, selectedIds: [id], panelScope: "room" })
    },

    setFocusedRoom: (id) => {
      if (id === null) {
        set({ focusedRoomId: null, mode: "select", status: MODE_STATUS.select })
        return
      }
      const room = get().rooms.find((r) => r.id === id)
      if (!room || get().readOnly) return
      set({
        focusedRoomId: id,
        selected: id,
        selectedIds: [id],
        mode: "select",
        status: `Editing ${room.name} — drag corners and walls, add deductions; Esc or Done to finish`,
      })
    },
    setStatus: (status) => set({ status }),

    addVertex: (pt) => mutate({ draft: [...get().draft, pt] }),

    removeLastVertex: () => {
      const { draft } = get()
      if (draft.length === 0) return
      mutate({ draft: draft.slice(0, -1) })
    },

    cancelDraft: () => {
      const s = get()
      if (s.draft.length === 0) return
      mutate({
        draft: [],
        rooms: restoredRooms(s),
        pendingRestore: null,
        ...(s.pendingRestore ? { mode: "select" as Mode } : {}),
        status: s.pendingRestore
          ? `Redraw cancelled — ${s.pendingRestore.name} restored`
          : "Trace cancelled",
      })
    },

    closeDraft: () => {
      const s = get()
      const draft = s.draft
      if (draft.length < 3) return
      if (lastCommitted === draft) return
      if (s.readOnly) return
      lastCommitted = draft
      const sheetId = s.activeSheetId ?? SAMPLE_SHEET_KEY
      const sheetRooms = s.rooms.filter((r) => r.sheetId === sheetId)

      if (s.mode === "room") {
        // Closing a redraw recreates the original room — same identity
        // (client_id, name, product, waste, holes), new outline.
        if (s.pendingRestore) {
          const orig = s.pendingRestore
          mutate({
            rooms: [ ...s.rooms, { ...orig, outline: draft } ],
            // A redrawn outline reshapes every cut the room produces — its
            // pins are stale (re-solve contract). Seams survive: they are
            // anchor-relative and stay draggable.
            cutPlans: orig.seams ? pruneRoomPins(s.cutPlans, orig.id) : s.cutPlans,
            draft: [],
            pendingRestore: null,
            mode: "select",
            status: `${orig.name} redrawn (${draft.length} corners)`,
          })
          set({ selected: orig.id, selectedIds: [orig.id] })
          return
        }
        const seq = s.roomSeq + 1
        mutate({
          rooms: [
            ...s.rooms,
            {
              id: crypto.randomUUID(),
              sheetId,
              name: `Room ${seq}`,
              outline: draft,
              holes: [],
              waste: 0,
              product: null,
              subfloor: null,
              grout: null,
              notes: [],
              multiplier: 1,
              wasteMode: null,
              seams: null,
            },
          ],
          roomSeq: seq,
          draft: [],
          status: `Room ${seq} traced (${draft.length} vertices)`,
        })
      } else if (s.mode === "deduct") {
        const c = centroid(draft)
        // In focus mode, deductions only land in the focused room; anywhere
        // else they'd silently punch a hole in a dimmed neighbor.
        const candidates = s.focusedRoomId
          ? sheetRooms.filter((r) => r.id === s.focusedRoomId)
          : sheetRooms
        const target = candidates.find(
          (r) => pointInRing(c, r.outline) || pointInRing(draft[0], r.outline)
        )
        if (!target) {
          mutate({
            draft: [],
            status: s.focusedRoomId
              ? "Deduction discarded — outside the room being edited"
              : "Deduction discarded — not inside any room",
          })
          return
        }
        mutate({
          rooms: s.rooms.map((r) =>
            r.id === target.id ? { ...r, holes: [...r.holes, draft] } : r
          ),
          draft: [],
          status: `Deduction added to ${target.name}`,
        })
      }
    },

    commitRect: (a, b) => {
      const s = get()
      if (s.readOnly) return
      if (s.mode !== "room" && s.mode !== "deduct") return
      // The gesture only arms on an empty draft (mid-trace drags are inert),
      // and this guard keeps that invariant even if a caller slips.
      if (s.draft.length > 0) return
      const ring = rectRing(a, b)
      if (!ring) return
      // Plain set, not mutate: the draft here is transient scaffolding, so
      // the one undo entry is closeDraft's commit — undoing a dragged
      // rectangle behaves exactly like undoing a clicked-shut trace.
      set({ draft: ring })
      get().closeDraft()
    },

    setCalibPoint: (pt) => {
      const { calibPts } = activeCalibration()
      const next = calibPts.length >= 2 ? [pt] : [...calibPts, pt]
      setCalibration(
        { ftPerPx: activeCalibration().ftPerPx, calibPts: next },
        {
          status:
            next.length === 2
              ? "Now enter the real distance and Apply"
              : "Click the second end of the dimension",
        }
      )
    },

    applyCalibration: (input) => {
      const { calibPts } = activeCalibration()
      if (calibPts.length !== 2) return
      const ft = parseFtIn(input)
      if (!ft || ft <= 0) {
        set({ status: `Could not parse "${input}" — try 26 or 26'0"` })
        return
      }
      const px = Math.hypot(calibPts[1][0] - calibPts[0][0], calibPts[1][1] - calibPts[0][1])
      setCalibration(
        { ftPerPx: ft / px, calibPts },
        { status: `Calibrated: ${ft.toFixed(3)} ft over ${px.toFixed(1)} px` }
      )
    },

    clearSheetCalibration: (sheetId) =>
      set((s) => {
        if (!(sheetId in s.calibBySheet)) return {}
        const calibBySheet = { ...s.calibBySheet }
        delete calibBySheet[sheetId]
        return { calibBySheet }
      }),

    applyPresetScale: (ftPerPx, label) =>
      setCalibration(
        { ftPerPx, calibPts: [] },
        { status: `Scale set — ${label} (1 ft = ${(1 / ftPerPx).toFixed(1)} px)` }
      ),

    completeCropCalibration: (status) =>
      setCalibration({ ftPerPx: activeCalibration().ftPerPx, calibPts: [] }, { status }),

    renameRow: (id, name) => mutate(mapRows((r) => r.id === id, (r) => ({ ...r, name }))),

    setWaste: (id, waste) =>
      mutate({ rooms: get().rooms.map((r) => (r.id === id ? { ...r, waste } : r)) }),

    deleteRoom: (id) => {
      const s = get()
      mutate({
        rooms: s.rooms.filter((r) => r.id !== id),
        // Its surfaces survive — a shower wall is still on the schedule when
        // the bathroom polygon is retraced — but they detach to the tail in
        // the same undo step, which is exactly what the schedule now shows.
        surfaces: s.surfaces.map((x) =>
          x.parentRoomId === id ? { ...x, parentRoomId: null } : x
        ),
        // A deleted room's cuts are gone with it — so are their pins, and so
        // are its scoped add-ons (their scope must stay exactly-one, so they
        // cannot detach the way a surface does). Same undo step.
        addOns: s.addOns.filter((a) => a.parentRoomId !== id),
        cutPlans: pruneRoomPins(s.cutPlans, id),
        status: "Room deleted",
      })
      const remaining = s.selectedIds.filter((x) => x !== id)
      set({
        selectedIds: remaining,
        selected: s.selected === id ? (remaining[remaining.length - 1] ?? null) : s.selected,
      })
      if (s.focusedRoomId === id) set({ focusedRoomId: null })
    },

    assignProduct: (rowIds, product) => {
      const ids = new Set(rowIds)
      if (ids.size === 0) return
      const s = get()
      // Waste is a quantity concept, so it only lands on rooms — a surface
      // has no geometry to waste. The spread below is a no-op on surfaces.
      const waste = suggestedWaste(product.pattern, s.settings.wasteSuggestions)
      // A different product means different cuts (and a non-roll product
      // means none) — the room's seam plan only survives a same-family swap.
      const keepSeams = rollGood(product)
      let cutPlans = s.cutPlans
      for (const r of s.rooms) {
        if (ids.has(r.id) && r.seams && r.product?.id !== product.id) {
          cutPlans = pruneRoomPins(cutPlans, r.id)
        }
      }
      mutate({
        ...mapRows(
          (r) => ids.has(r.id),
          (r) => ({
            ...r,
            product: { ...product },
            ...(waste != null ? { waste } : {}),
            ...("seams" in r && !keepSeams ? { seams: null } : {}),
          })
        ),
        cutPlans,
        status: `Product assigned to ${ids.size} row${ids.size === 1 ? "" : "s"}`,
      })
    },

    updateProduct: (id, patch) => {
      const waste =
        patch.pattern !== undefined
          ? suggestedWaste(patch.pattern, get().settings.wasteSuggestions)
          : null
      mutate(
        mapRows(
          (r) => r.id === id && r.product != null,
          (r) => ({
            ...r,
            product: { ...r.product!, ...patch },
            ...(waste != null ? { waste } : {}),
          })
        )
      )
    },

    // Overlay color is a property of the PRODUCT, not the row: one pick
    // recolors every row drawing that product (their snapshots share the
    // catalog id), in one undo step.
    setProductColor: (productId, color) => {
      mutate({
        ...mapRows(
          (r) => r.product?.id === productId,
          (r) => ({ ...r, product: { ...r.product!, overlay_color: color } })
        ),
        status: "Product color updated",
      })
    },

    setOverlayPreview: (preview) => set({ overlayPreview: preview }),

    commitOverlayPreview: () => {
      const preview = get().overlayPreview
      set({ overlayPreview: null })
      if (!preview) return
      const row = [...get().rooms, ...get().surfaces].find(
        (r) => r.product?.id === preview.productId
      )
      const current = row?.product
      if (!current) return
      if (
        (current.overlay_color ?? defaultOverlayColor(current.id)) === preview.color &&
        productFill(current) === preview.fill
      ) {
        return
      }
      mutate({
        ...mapRows(
          (r) => r.product?.id === preview.productId,
          (r) => ({
            ...r,
            product: { ...r.product!, overlay_color: preview.color, overlay_fill: preview.fill },
          })
        ),
        status: "Product overlay updated",
      })
    },

    clearProduct: (id) => {
      const s = get()
      mutate({
        ...mapRows((r) => r.id === id, (r) => ({ ...r, product: null, ...("seams" in r ? { seams: null } : {}) })),
        cutPlans: pruneRoomPins(s.cutPlans, id),
        status: "Product removed",
      })
      if (s.productPreview?.roomId === id) set({ productPreview: null })
      if (s.seamPreview?.roomId === id) set({ seamPreview: null })
    },

    setProductPreview: (preview) => set({ productPreview: preview }),

    setSpec: (id, patch) => mutate(mapRows((r) => r.id === id, (r) => ({ ...r, ...patch }))),

    setWasteMode: (id, mode) =>
      mutate({ rooms: get().rooms.map((r) => (r.id === id ? { ...r, wasteMode: mode } : r)) }),

    setMultiplier: (id, multiplier) => {
      const n = Math.min(Math.max(Math.round(multiplier) || 1, 1), 999)
      const s = get()
      mutate({
        rooms: s.rooms.map((r) => (r.id === id ? { ...r, multiplier: n } : r)),
        // Copies at index >= n no longer exist — their pins are stale.
        cutPlans: pruneRoomPins(s.cutPlans, id, (key) => Number(key.split(":")[1]) < n),
        status: n === 1 ? "Multiplier cleared" : `Room allocated ×${n} — waste pools across the copies`,
      })
    },

    setSettings: (settings) => set({ settings }),
    selectSeam: (seam) => set({ selectedSeam: seam }),

    proposeSeamLayout: (roomId) => {
      const s = get()
      const room = s.rooms.find((r) => r.id === roomId)
      if (!room || !rollGood(room.product)) return
      // Generator tools skip locked rooms (refinements) — the estimator
      // unlocks first, deliberately.
      if (room.seams?.locked) {
        set({ status: `${room.name}'s seams are locked — unlock to re-propose` })
        return
      }
      const ftPerPx = roomFtPerPx(room)
      const usableW = usableWidthFt(room.product, s.settings)
      if (ftPerPx == null || usableW == null) {
        set({ status: "Calibrate the sheet and set a roll width before planning seams" })
        return
      }
      const angle = room.seams?.angle ?? 0
      const fillSide = room.seams?.fill_side ?? "end"
      const outlineLay = layRing(room.outline, room.outline[0], angle, ftPerPx)
      mutate({
        rooms: s.rooms.map((r) =>
          r.id === roomId ? { ...r, seams: proposeSeams(outlineLay, angle, usableW, fillSide) } : r
        ),
        cutPlans: pruneRoomPins(s.cutPlans, roomId),
        status: `Proposed seam layout for ${room.name} — drag seams to adjust`,
      })
    },

    clearSeams: (roomId) => {
      const s = get()
      mutate({
        rooms: s.rooms.map((r) => (r.id === roomId ? { ...r, seams: null } : r)),
        cutPlans: pruneRoomPins(s.cutPlans, roomId),
        status: "Seam plan removed",
      })
      if (s.seamPreview?.roomId === roomId) set({ seamPreview: null })
      if (s.selectedSeam?.roomId === roomId) set({ selectedSeam: null })
    },

    // Re-proposes from the other side — a generator action, so it respects
    // the room seam lock via proposeSeamLayout's own guard.
    switchFillSide: (roomId) => {
      const s = get()
      const room = s.rooms.find((r) => r.id === roomId)
      if (!room?.seams || !rollGood(room.product)) return
      if (room.seams.locked) {
        set({ status: `${room.name}'s seams are locked — unlock to re-propose` })
        return
      }
      const ftPerPx = roomFtPerPx(room)
      const usableW = usableWidthFt(room.product, s.settings)
      if (ftPerPx == null || usableW == null) return
      const fillSide = room.seams.fill_side === "end" ? "start" : "end"
      const outlineLay = layRing(room.outline, room.outline[0], room.seams.angle, ftPerPx)
      mutate({
        rooms: s.rooms.map((r) =>
          r.id === roomId
            ? { ...r, seams: proposeSeams(outlineLay, room.seams!.angle, usableW, fillSide) }
            : r
        ),
        cutPlans: pruneRoomPins(s.cutPlans, roomId),
        status: `Fill strip moved to the ${fillSide === "end" ? "far" : "near"} side`,
      })
    },

    // Rotating the lay direction restructures every strip, so it re-proposes
    // at the new angle — announced, never silent.
    setLayDirection: (roomId, angle) => {
      const s = get()
      const room = s.rooms.find((r) => r.id === roomId)
      if (!room || !rollGood(room.product)) return
      if (room.seams?.locked) {
        set({ status: `${room.name}'s seams are locked — unlock to change lay direction` })
        return
      }
      const normalized = ((Math.round(angle) % 360) + 360) % 360
      const ftPerPx = roomFtPerPx(room)
      const usableW = usableWidthFt(room.product, s.settings)
      if (ftPerPx == null || usableW == null) return
      const fillSide = room.seams?.fill_side ?? "end"
      const outlineLay = layRing(room.outline, room.outline[0], normalized, ftPerPx)
      mutate({
        rooms: s.rooms.map((r) =>
          r.id === roomId
            ? { ...r, seams: proposeSeams(outlineLay, normalized, usableW, fillSide) }
            : r
        ),
        cutPlans: pruneRoomPins(s.cutPlans, roomId),
        status: `Lay direction set to ${normalized}° — layout re-proposed`,
      })
    },

    addSeamAt: (roomId, posFt) => {
      seamEdit(
        roomId,
        (seams) => {
          // The new boundary splits the strip it lands in; cross seams above
          // it follow their material to the higher strip index.
          const stripIndex = seams.offsets.filter((o) => o.pos_ft < posFt).length
          return {
            ...seams,
            offsets: [...seams.offsets, { pos_ft: posFt, locked: false }].sort(
              (a, b) => a.pos_ft - b.pos_ft
            ),
            cross: seams.cross.map((c) => (c.strip > stripIndex ? { ...c, strip: c.strip + 1 } : c)),
          }
        },
        "Seam added — drag it into place"
      )
      // Select the new seam so it's visibly grabbable right away.
      const added = get().rooms.find((r) => r.id === roomId)?.seams
      if (added) {
        const index = added.offsets.findIndex((o) => o.pos_ft === posFt)
        if (index !== -1) set({ selectedSeam: { roomId, kind: "offset", index } })
      }
    },

    addCrossSeamAt: (roomId, strip, posFt) => {
      seamEdit(
        roomId,
        (seams) => ({ ...seams, cross: [...seams.cross, { strip, pos_ft: posFt }] }),
        "T-seam added — drag it along the roll"
      )
      const added = get().rooms.find((r) => r.id === roomId)?.seams
      if (added?.cross.length) {
        set({ selectedSeam: { roomId, kind: "cross", index: added.cross.length - 1 } })
      }
    },

    removeSeam: (roomId, kind, index) => {
      if (kind === "cross") {
        seamEdit(
          roomId,
          (seams) => ({ ...seams, cross: seams.cross.filter((_, i) => i !== index) }),
          "T-seam removed"
        )
      } else {
        seamEdit(
          roomId,
          (seams) => ({
            ...seams,
            offsets: seams.offsets.filter((_, i) => i !== index),
            // Boundary i separated strips i and i+1 — they merge into one,
            // and every cross seam above slides down with its strip.
            cross: seams.cross.map((c) => (c.strip > index ? { ...c, strip: c.strip - 1 } : c)),
          }),
          "Seam removed"
        )
      }
      if (get().selectedSeam?.roomId === roomId) set({ selectedSeam: null })
    },

    // Locking is not a geometry change: pins stay, auto stays — only the
    // wall flag flips.
    toggleSeamLock: (roomId, index) => {
      const s = get()
      const room = s.rooms.find((r) => r.id === roomId)
      if (!room?.seams) return
      const next = room.seams.offsets.map((o, i) => (i === index ? { ...o, locked: !o.locked } : o))
      mutate({
        rooms: s.rooms.map((r) => (r.id === roomId ? { ...r, seams: { ...r.seams!, offsets: next } } : r)),
        status: next[index].locked ? "Seam locked — drags stop here" : "Seam unlocked",
      })
    },

    toggleRoomSeamLock: (roomId) => {
      const s = get()
      const room = s.rooms.find((r) => r.id === roomId)
      if (!room?.seams) return
      const locked = !room.seams.locked
      mutate({
        rooms: s.rooms.map((r) => (r.id === roomId ? { ...r, seams: { ...r.seams!, locked } } : r)),
        status: locked
          ? `${room.name}'s seams locked — generator tools will skip this room`
          : `${room.name}'s seams unlocked`,
      })
    },

    dragSeamPreview: (roomId, offsetIndex, targetFt) => {
      const s = get()
      if (s.readOnly) return
      const room = s.rooms.find((r) => r.id === roomId)
      if (!room?.seams) return
      const ftPerPx = roomFtPerPx(room)
      const usableW = usableWidthFt(room.product, s.settings)
      if (ftPerPx == null || usableW == null) return
      // Always drag from the COMMITTED offsets — recomputing the whole push
      // from the original state each move keeps the drag deterministic and
      // drift-free, exactly like the angle preview.
      const outlineLay = layRing(room.outline, room.outline[0], room.seams.angle, ftPerPx)
      const offsets = dragSeam(
        room.seams.offsets,
        offsetIndex,
        targetFt,
        usableW,
        laySpan(outlineLay)
      )
      set({ seamPreview: { roomId, seams: { ...room.seams, offsets, auto: false } } })
    },

    dragCrossPreview: (roomId, crossIndex, posFt) => {
      const s = get()
      if (s.readOnly) return
      const room = s.rooms.find((r) => r.id === roomId)
      if (!room?.seams) return
      set({
        seamPreview: {
          roomId,
          seams: {
            ...room.seams,
            cross: room.seams.cross.map((c, i) => (i === crossIndex ? { ...c, pos_ft: posFt } : c)),
            auto: false,
          },
        },
      })
    },

    commitSeamPreview: () => {
      const preview = get().seamPreview
      if (!preview) return
      set({ seamPreview: null })
      seamEdit(preview.roomId, () => preview.seams, "Seam moved")
    },

    cancelSeamPreview: () => set({ seamPreview: null }),

    pinCut: (productId, cutKey, pin) => {
      const s = get()
      const plan = s.cutPlans[productId] ?? { pins: {}, locks: [] }
      const pins = { ...plan.pins }
      if (pin == null) delete pins[cutKey]
      else pins[cutKey] = pin
      mutate({
        cutPlans: { ...s.cutPlans, [productId]: { ...plan, pins } },
        status: pin == null ? "Cut released to auto-placement" : `Cut placed on roll ${pin.roll + 1}`,
      })
    },

    selectSurface: (id) =>
      // A surface has no geometry, so selecting one must clear the room
      // selection outright — otherwise the canvas would keep drawing handles
      // for a row the inspector is no longer showing.
      set(id ? { selectedSurfaceId: id, selected: null, selectedIds: [], focusedRoomId: null, panelScope: "room" }
             : { selectedSurfaceId: null }),

    addSurface: (parentRoomId) => {
      const s = get()
      if (s.readOnly) return
      const parent = parentRoomId && s.rooms.some((r) => r.id === parentRoomId) ? parentRoomId : null
      const surface: Surface = {
        id: crypto.randomUUID(),
        name: nextSurfaceName(s.surfaces),
        parentRoomId: parent,
        product: null,
        subfloor: null,
        grout: null,
        notes: [],
        manualQty: null,
      }
      // Append: scheduleRows groups by parent, so array order only decides
      // sibling order — a new surface belongs last among its siblings.
      mutate({ surfaces: [...s.surfaces, surface], status: "Surface added" })
      set({
        selectedSurfaceId: surface.id,
        selected: null,
        selectedIds: [],
        focusedRoomId: null,
        panelScope: "room",
      })
    },

    deleteSurface: (id) => {
      const s = get()
      mutate({
        surfaces: s.surfaces.filter((x) => x.id !== id),
        // Its scoped add-ons go with it, in the same undo step.
        addOns: s.addOns.filter((a) => a.parentSurfaceId !== id),
        status: "Surface deleted",
      })
      if (s.selectedSurfaceId === id) set({ selectedSurfaceId: null })
    },

    setSurfaceQty: (id, qty) =>
      mutate({
        surfaces: get().surfaces.map((x) => (x.id === id ? { ...x, manualQty: qty } : x)),
      }),

    addAddOn: (draft) => {
      const s = get()
      if (s.readOnly) return
      const addOn: EditorAddOn = { ...draft, id: crypto.randomUUID() }
      mutate({ addOns: [...s.addOns, addOn], status: "Add-on added" })
    },

    updateAddOn: (id, patch) =>
      mutate({
        addOns: get().addOns.map((a) => (a.id === id ? { ...a, ...patch } : a)),
        status: "Add-on updated",
      }),

    removeAddOn: (id) =>
      mutate({ addOns: get().addOns.filter((a) => a.id !== id), status: "Add-on removed" }),

    addOnRules: [],

    setAddOnRules: (rules) => set({ addOnRules: rulesFromServer(rules) }),

    saveAddOnRule: async (draft) => {
      const s = get()
      if (s.readOnly) return { ok: false }
      const prev = s.addOnRules
      const body = {
        product_add_on: {
          product_id: draft.productId,
          item: draft.item,
          calc_method: draft.calc_method,
          coverage_rate: draft.coverage_rate,
          min_qty: draft.min_qty,
          count_qty: draft.count_qty,
          manual_qty: draft.manual_qty,
          subfloor_condition: draft.subfloorCondition,
          print_on_schedule: draft.printOnSchedule,
          ...(draft.id == null
            ? { position: prev.filter((r) => r.productId === draft.productId).length }
            : {}),
        },
      }
      // Optimistic: the panel shows the rule immediately; the server's
      // returned list replaces it (or the rollback below removes it). The
      // sequence counter makes the LATEST mutation authoritative — two rapid
      // toggles can complete out of order, and an older response must not
      // clobber a newer confirmed write.
      const seq = ++ruleMutationSeq
      const temp: AddOnRule = {
        ...draft,
        id: draft.id ?? -Date.now(),
        position: prev.length,
      }
      set({
        addOnRules:
          draft.id != null
            ? prev.map((r) => (r.id === draft.id ? { ...r, ...draft, id: draft.id! } : r))
            : [...prev, temp],
      })
      type RulesResponse = {
        ok: boolean
        product_add_ons?: WireProductAddOn[]
        errors?: Record<string, string>
      }
      try {
        const { status, data } =
          draft.id != null
            ? await requestJSON<RulesResponse>("PATCH", `/product_add_ons/${draft.id}`, body)
            : await postJSON<RulesResponse>("/product_add_ons", body)
        if (status === 200 && data?.ok && data.product_add_ons) {
          if (seq === ruleMutationSeq) set({ addOnRules: rulesFromServer(data.product_add_ons) })
          return { ok: true }
        }
        if (seq === ruleMutationSeq) {
          set({ addOnRules: prev, status: "Rule save failed — the takeoff itself is unaffected" })
        }
        return { ok: false, errors: data?.errors }
      } catch {
        // Network failure: fetch rejected before any response. Roll the
        // optimistic rule back — a phantom row that never reached the server
        // must not sit in the panel looking saved.
        if (seq === ruleMutationSeq) {
          set({ addOnRules: prev, status: "Rule save failed — check the connection" })
        }
        return { ok: false, errors: { base: "The rule could not reach the server." } }
      }
    },

    deleteAddOnRule: async (id) => {
      const s = get()
      if (s.readOnly) return
      const prev = s.addOnRules
      const seq = ++ruleMutationSeq
      set({ addOnRules: prev.filter((r) => r.id !== id) })
      type RulesResponse = { ok: boolean; product_add_ons?: WireProductAddOn[] }
      try {
        const { status, data } = await requestJSON<RulesResponse>(
          "DELETE",
          `/product_add_ons/${id}`,
          {}
        )
        if (status === 200 && data?.ok && data.product_add_ons) {
          if (seq === ruleMutationSeq) set({ addOnRules: rulesFromServer(data.product_add_ons) })
        } else if (seq === ruleMutationSeq) {
          set({ addOnRules: prev, status: "Rule delete failed — nothing changed" })
        }
      } catch {
        if (seq === ruleMutationSeq) {
          set({ addOnRules: prev, status: "Rule delete failed — check the connection" })
        }
      }
    },

    moveRowAfter: (id, afterRowId) => {
      const s = get()
      if (afterRowId === id) return

      const movingRoom = s.rooms.find((r) => r.id === id)
      if (movingRoom) {
        const rest = s.rooms.filter((r) => r.id !== id)
        // Dropping onto a surface means "after the room that owns it".
        const anchorId = afterRowId
          ? (s.surfaces.find((x) => x.id === afterRowId)?.parentRoomId ?? afterRowId)
          : null
        const anchor = anchorId ? rest.findIndex((r) => r.id === anchorId) : -1
        if (afterRowId && anchor === -1 && anchorId !== null) return
        const next = [...rest]
        next.splice(anchor + 1, 0, movingRoom)
        mutate({ rooms: next, status: `${movingRoom.name} moved` })
        return
      }

      const moving = s.surfaces.find((x) => x.id === id)
      if (!moving) return

      const rest = s.surfaces.filter((x) => x.id !== id)
      const afterSurface = afterRowId ? rest.find((x) => x.id === afterRowId) : undefined
      const afterRoom =
        afterRowId && !afterSurface ? s.rooms.find((r) => r.id === afterRowId) : undefined
      if (afterRowId && !afterSurface && !afterRoom) return

      const parentRoomId = afterSurface ? afterSurface.parentRoomId : (afterRoom?.id ?? null)

      let index: number
      if (afterSurface) {
        // Next sibling of the row it was dropped on.
        index = rest.indexOf(afterSurface) + 1
      } else if (afterRoom) {
        // First surface of that room — insert ahead of its current first.
        const first = rest.findIndex((x) => x.parentRoomId === afterRoom.id)
        index = first === -1 ? rest.length : first
      } else {
        index = rest.length // the unattached tail
      }

      const next = [...rest]
      next.splice(index, 0, { ...moving, parentRoomId })
      mutate({
        surfaces: next,
        status: parentRoomId ? `${moving.name} moved` : `${moving.name} detached`,
      })
    },

    moveVertex: (id, ring, index, pt) => {
      const moveIn = (room: Room): Room => {
        if (ring === "outline") {
          return { ...room, outline: room.outline.map((v, i) => (i === index ? pt : v)) }
        }
        return {
          ...room,
          holes: room.holes.map((hole, h) =>
            h === ring ? hole.map((v, i) => (i === index ? pt : v)) : hole
          ),
        }
      }
      const s = get()
      const seamed = ring === "outline" && s.rooms.find((r) => r.id === id)?.seams != null
      mutate({
        rooms: s.rooms.map((r) => (r.id === id ? moveIn(r) : r)),
        // An outline edit reshapes the room's cuts — stale pins drop (the
        // re-solve contract). Deduction holes never enter cut math, so hole
        // edits change nothing on the roll.
        ...(seamed ? { cutPlans: pruneRoomPins(s.cutPlans, id) } : {}),
        status: "Corner adjusted",
      })
    },

    // Split an edge: the new point lands between ring[edgeIndex] and its
    // successor. Splicing at edgeIndex + 1 is correct for the closing edge
    // too (splice at n appends, which is between ring[n-1] and ring[0]).
    insertVertex: (id, ring, edgeIndex, pt) => {
      const insertIn = (room: Room): Room => {
        if (ring === "outline") {
          const outline = [...room.outline]
          outline.splice(edgeIndex + 1, 0, pt)
          return { ...room, outline }
        }
        return {
          ...room,
          holes: room.holes.map((hole, h) => {
            if (h !== ring) return hole
            const next = [...hole]
            next.splice(edgeIndex + 1, 0, pt)
            return next
          }),
        }
      }
      const s = get()
      const seamed = ring === "outline" && s.rooms.find((r) => r.id === id)?.seams != null
      mutate({
        rooms: s.rooms.map((r) => (r.id === id ? insertIn(r) : r)),
        ...(seamed ? { cutPlans: pruneRoomPins(s.cutPlans, id) } : {}),
        status: "Corner added",
      })
    },

    // Remove a corner; the two adjacent walls merge. Outlines keep a 3-corner
    // minimum; a 3-corner deduction collapses entirely instead.
    deleteVertex: (id, ring, index) => {
      const s = get()
      const room = s.rooms.find((r) => r.id === id)
      if (!room) return
      if (ring === "outline") {
        if (room.outline.length <= 3) {
          set({ status: "A room needs at least 3 corners" })
          return
        }
        mutate({
          rooms: s.rooms.map((r) =>
            r.id === id ? { ...r, outline: r.outline.filter((_, i) => i !== index) } : r
          ),
          ...(room.seams ? { cutPlans: pruneRoomPins(s.cutPlans, id) } : {}),
          status: "Corner removed",
        })
        return
      }
      const hole = room.holes[ring]
      if (!hole) return
      if (hole.length <= 3) {
        mutate({
          rooms: s.rooms.map((r) =>
            r.id === id ? { ...r, holes: r.holes.filter((_, h) => h !== ring) } : r
          ),
          status: "Deduction removed",
        })
      } else {
        mutate({
          rooms: s.rooms.map((r) =>
            r.id === id
              ? {
                  ...r,
                  holes: r.holes.map((h, hi) =>
                    hi === ring ? h.filter((_, i) => i !== index) : h
                  ),
                }
              : r
          ),
          status: "Corner removed",
        })
      }
    },

    // Push/pull a wall: both endpoints of the edge translate together.
    translateEdge: (id, ring, edgeIndex, dx, dy) => {
      if (dx === 0 && dy === 0) return
      const moveIn = (room: Room): Room => {
        const target = ring === "outline" ? room.outline : room.holes[ring]
        if (!target) return room
        const moved = translateEdgePoints(target, edgeIndex, dx, dy)
        return ring === "outline"
          ? { ...room, outline: moved }
          : { ...room, holes: room.holes.map((h, hi) => (hi === ring ? moved : h)) }
      }
      const s = get()
      const seamed = ring === "outline" && s.rooms.find((r) => r.id === id)?.seams != null
      mutate({
        rooms: s.rooms.map((r) => (r.id === id ? moveIn(r) : r)),
        ...(seamed ? { cutPlans: pruneRoomPins(s.cutPlans, id) } : {}),
        status: "Wall moved",
      })
    },

    openWall: (id, index, side) => {
      const s = get()
      const room = s.rooms.find((r) => r.id === id)
      if (!room || s.readOnly) return
      if (index < 0 || index >= room.outline.length) return
      mutate({
        rooms: s.rooms.filter((r) => r.id !== id),
        draft: openRing(room.outline, index, side),
        pendingRestore: room,
        mode: "room",
        status:
          `Opened a wall of ${room.name} — trace from the selected corner; ` +
          `close on the far corner to finish (Esc restores the original)`,
      })
      if (s.focusedRoomId === id) set({ focusedRoomId: null })
    },

    // NOTE: no cut-pin invalidation here — seams are anchor-relative and the
    // anchor translates with the room, so every cut keeps its exact shape.
    translateRoom: (id, dx, dy) => {
      if (dx === 0 && dy === 0) return
      const shift = (ring: Pt[]): Pt[] => translateRing(ring, dx, dy)
      mutate({
        rooms: get().rooms.map((r) =>
          r.id === id
            ? { ...r, outline: shift(r.outline), holes: r.holes.map(shift) }
            : r
        ),
        status: "Room moved",
      })
    },

    undo: () => {
      if (get().readOnly) return
      set((s) => {
        const prev = s.undoStack[s.undoStack.length - 1]
        if (!prev) return s
        return {
          ...prev,
          undoStack: s.undoStack.slice(0, -1),
          redoStack: [...s.redoStack, takeSnapshot(s)],
          docSeq: s.docSeq + 1,
          saveState: "dirty",
          status: "Undid last change",
        }
      })
    },

    redo: () => {
      if (get().readOnly) return
      set((s) => {
        const next = s.redoStack[s.redoStack.length - 1]
        if (!next) return s
        return {
          ...next,
          redoStack: s.redoStack.slice(0, -1),
          undoStack: [...s.undoStack, takeSnapshot(s)],
          docSeq: s.docSeq + 1,
          saveState: "dirty",
          status: "Redid change",
        }
      })
    },

    reset: () =>
      set({
        ...initialSnapshot,
        mode: "select",
        selected: null,
        selectedIds: [],
        status: "Pick a tool to begin",
        undoStack: [],
        redoStack: [],
        activeSheetId: null,
        readOnly: false,
        docSeq: 0,
        serverRevision: null,
        saveState: "idle",
        productPreview: null,
        focusedRoomId: null,
        selectedSurfaceId: null,
        selectedSeam: null,
        seamPreview: null,
        overlayPreview: null,
        panelScope: "takeoff",
        assigningProductId: null,
      }),

    // Replace the document with the server's copy — used on page load, on
    // restore, and when a viewer's poll sees a new revision. Never pushes
    // undo; a hydrate is a new baseline, not an edit.
    hydrate: ({ rooms, surfaces, addOns, cutPlans, sheets, revision, activeSheetId }) => {
      const editorRooms = roomsFromServer(rooms)
      set({
        rooms: editorRooms,
        surfaces: surfacesFromServer(surfaces),
        addOns: addOnsFromServer(addOns),
        cutPlans: cutPlans ?? {},
        draft: [],
        calibBySheet: calibrationsFromSheets(sheets),
        roomSeq: nextRoomSeq(editorRooms),
        undoStack: [],
        redoStack: [],
        selected: null,
        selectedIds: [],
        activeSheetId,
        serverRevision: revision,
        saveState: "saved",
        productPreview: null,
        focusedRoomId: null,
        selectedSurfaceId: null,
        selectedSeam: null,
        seamPreview: null,
        overlayPreview: null,
        assigningProductId: null,
        status: "Pick a tool to begin",
      })
    },

    // Rooms persist across sheets; draft/selection/undo are per-visit-per-sheet.
    setActiveSheet: (id) => {
      const s = get()
      if (s.activeSheetId === id) return
      set({
        activeSheetId: id,
        draft: [],
        rooms: restoredRooms(s),
        pendingRestore: null,
        selected: null,
        selectedIds: [],
        undoStack: [],
        redoStack: [],
        mode: "select",
        productPreview: null,
        focusedRoomId: null,
        selectedSurfaceId: null,
        selectedSeam: null,
        seamPreview: null,
        overlayPreview: null,
        assigningProductId: null,
        status: MODE_STATUS.select,
      })
    },

    setReadOnly: (readOnly) => {
      const s = get()
      set(
        readOnly
          ? {
              readOnly,
              mode: "select",
              draft: [],
              rooms: restoredRooms(s),
              pendingRestore: null,
              selected: null,
              selectedIds: [],
              productPreview: null,
              focusedRoomId: null,
              selectedSurfaceId: null,
              selectedSeam: null,
              seamPreview: null,
              overlayPreview: null,
              assigningProductId: null,
            }
          : { readOnly }
      )
    },

    markSaving: () => set({ saveState: "saving" }),

    // Only settle to "saved" if nothing changed while the request was in
    // flight — otherwise stay dirty so the autosave loop goes again.
    markSaved: (revision, savedSeq) =>
      set((s) => ({
        serverRevision: revision,
        saveState: s.docSeq === savedSeq ? "saved" : "dirty",
      })),

    markSaveError: (detail) =>
      set(detail ? { saveState: "error", status: `Not saving: ${detail}` } : { saveState: "error" }),
  }
})
